import { useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useIframeSdk } from '@whop/react';
import { Badge, Button, Heading, Text } from 'frosted-ui';
import { ArrowUpRightFromSquare16, Checkmark16 } from '@frosted-ui/icons';
import { Rise } from '../components/animation';
import LoadingScreen from '../components/LoadingScreen';
import MemberOfferCard from '../components/MemberOfferCard';
import { api } from '../services/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import type { ExperienceOffer, ExperienceView } from '../types';

type PurchaseState =
  | { kind: 'idle' }
  | { kind: 'purchasing' }
  | { kind: 'success' }
  | { kind: 'error'; message: string };

/**
 * inAppPurchase resolves `{ status: 'error', error }` both when the member
 * closes Whop's modal and when checkout really fails; the SDK has no
 * separate cancel status, so a cancel is told apart by its message.
 */
export function isPurchaseCancel(error: string | undefined): boolean {
  return /cancel|clos|dismiss|abort/i.test(error ?? '');
}

/** Open a whop.com page inside Whop, else in a new tab. */
function useOpenInWhop() {
  const iframeSdk = useIframeSdk();
  return (url: string) =>
    iframeSdk.openExternalUrl({ url }).catch((err: unknown) => {
      console.error('openExternalUrl failed:', err);
      window.open(url, '_blank', 'noopener,noreferrer');
    });
}

function Page({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        minHeight: '100dvh',
        boxSizing: 'border-box',
        display: 'flex',
        justifyContent: 'center',
        padding: 'var(--space-5) var(--space-4)',
        backgroundColor: 'var(--color-background)',
      }}
    >
      <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {children}
      </div>
    </div>
  );
}

/** A quiet centered message: nothing to sell, already a member, or an error. */
function Notice({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <Rise>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-7) var(--space-4)',
        }}
      >
        <Heading size="5" as="h1">
          {title}
        </Heading>
        {body && (
          <Text size="2" color="gray" style={{ maxWidth: 320 }}>
            {body}
          </Text>
        )}
        {action && <div style={{ marginTop: 'var(--space-3)' }}>{action}</div>}
      </div>
    </Rise>
  );
}

function OfferView({ offer }: { offer: ExperienceOffer }) {
  const iframeSdk = useIframeSdk();
  const openInWhop = useOpenInWhop();
  const [purchase, setPurchase] = useState<PurchaseState>({ kind: 'idle' });

  async function handleUpgrade() {
    setPurchase({ kind: 'purchasing' });
    try {
      const result = await iframeSdk.inAppPurchase({
        planId: offer.planId,
        id: offer.checkoutConfigId,
      });
      if (result.status === 'ok') {
        setPurchase({ kind: 'success' });
      } else if (isPurchaseCancel(result.error)) {
        // They closed the checkout: nothing went wrong, just start over.
        setPurchase({ kind: 'idle' });
      } else {
        console.error('inAppPurchase failed:', result.error);
        setPurchase({ kind: 'error', message: "Checkout didn't go through." });
      }
    } catch (err) {
      console.error('inAppPurchase failed:', err);
      setPurchase({ kind: 'error', message: "Checkout couldn't open here." });
    }
  }

  // Whop-hosted checkout for the same variant, still inside Whop.
  function openHostedCheckout() {
    openInWhop(offer.purchaseUrl);
  }

  const boughtName = offer.packageLabel || offer.productName;

  return (
    <>
      {offer.accessLevel === 'admin' && (
        <Rise>
          {/* Badges don't wrap by default; this one is a sentence and must fit 390px. */}
          <Badge
            size="2"
            color="amber"
            variant="soft"
            style={{
              alignSelf: 'flex-start',
              maxWidth: '100%',
              flexShrink: 1,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              borderRadius: 'var(--radius-4)',
            }}
          >
            {offer.isWinner
              ? 'Admin preview — members see your winning offer'
              : 'Admin preview — each member sees one of your offers'}
          </Badge>
        </Rise>
      )}
      <Rise delay={offer.accessLevel === 'admin' ? 60 : 0}>
        <MemberOfferCard
          sellerName={offer.sellerName}
          productName={boughtName}
          benefits={offer.benefits}
          price={offer.price}
          currency={offer.currency}
          interval={offer.interval}
          trialDays={offer.trialDays}
          initialFee={offer.initialFee}
          action={
            purchase.kind === 'success' ? (
              <div
                className="vara2-step-in"
                style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
              >
                <div
                  role="status"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--space-2)',
                    minHeight: 48,
                    borderRadius: 'var(--radius-4)',
                    backgroundColor: 'var(--green-a3)',
                    color: 'var(--green-11)',
                  }}
                >
                  <Checkmark16 />
                  <Text size="3" weight="medium">
                    You're in! Your access is being set up.
                  </Text>
                </div>
                {offer.productUrl && (
                  <Button
                    size="4"
                    variant="solid"
                    style={{ width: '100%', minHeight: 52 }}
                    onClick={() => openInWhop(offer.productUrl!)}
                  >
                    Open {boughtName || 'it'}
                    <ArrowUpRightFromSquare16 />
                  </Button>
                )}
              </div>
            ) : (
              <Button
                size="4"
                variant="solid"
                style={{ width: '100%', minHeight: 52 }}
                onClick={handleUpgrade}
                loading={purchase.kind === 'purchasing'}
                disabled={purchase.kind === 'purchasing'}
              >
                Upgrade
              </Button>
            )
          }
        >
          {purchase.kind === 'error' && (
            <div
              className="vara2-step-in"
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'var(--space-2)' }}
            >
              <Text size="2" color="gray">
                {purchase.message}
              </Text>
              <Button size="2" variant="soft" onClick={openHostedCheckout}>
                Open checkout instead
                <ArrowUpRightFromSquare16 />
              </Button>
            </div>
          )}
        </MemberOfferCard>
      </Rise>
    </>
  );
}

/**
 * Member-facing experience view ("Inside your whop"). Whop loads it at
 * /experiences/:experienceId inside the member's whop; the user token header
 * identifies the member, and the purchase runs in Whop's in-app modal.
 */
export default function ExperienceOfferPage() {
  const { experienceId } = useParams<{ experienceId: string }>();
  const openInWhop = useOpenInWhop();
  const [view, setView] = useState<ExperienceView | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  useDocumentTitle(view && view.state !== 'empty' ? view.sellerName : null);

  useEffect(() => {
    if (!experienceId) return;
    let cancelled = false;
    api
      .getExperienceOffer(experienceId)
      .then((data) => {
        if (!cancelled) setView(data);
      })
      .catch((err: unknown) => {
        console.error('Failed to load offer:', err);
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Something went wrong');
      });
    return () => {
      cancelled = true;
    };
  }, [experienceId]);

  if (!view && !loadError) return <LoadingScreen />;

  if (loadError || !view) {
    return (
      <Page>
        <Notice title="This page didn't load" body="Close it and open it again in a moment." />
      </Page>
    );
  }

  if (view.state === 'member') {
    return (
      <Page>
        <Notice
          title="You're already a member"
          body={view.productName ? `You have ${view.productName}. Nothing else to buy here.` : undefined}
          action={
            view.productUrl ? (
              <Button size="3" variant="soft" onClick={() => openInWhop(view.productUrl!)}>
                Open {view.productName || 'it'}
                <ArrowUpRightFromSquare16 />
              </Button>
            ) : undefined
          }
        />
      </Page>
    );
  }

  if (view.state === 'empty') {
    const dashboardUrl = view.dashboardUrl;
    return (
      <Page>
        {view.accessLevel === 'admin' && dashboardUrl && view.pausedTest ? (
          <Notice
            title="Your test is paused"
            body={`“${view.pausedTest.name}” uses this tab. Free members see nothing here until you resume it.`}
            action={
              <Button size="3" variant="solid" onClick={() => openInWhop(dashboardUrl)}>
                Open Paywall Lab
                <ArrowUpRightFromSquare16 />
              </Button>
            }
          />
        ) : view.accessLevel === 'admin' && dashboardUrl ? (
          <Notice
            title="No test is using this tab"
            body="Create an “Inside your whop” price test and pick this product. Free members will see the offer here."
            action={
              <Button
                size="3"
                variant="solid"
                onClick={() => openInWhop(dashboardUrl)}
              >
                Create one
                <ArrowUpRightFromSquare16 />
              </Button>
            }
          />
        ) : (
          <Notice title="Nothing here yet" body="Check back soon." />
        )}
      </Page>
    );
  }

  return (
    <Page>
      <OfferView offer={view} />
    </Page>
  );
}
